import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { ShieldCheck, ArrowLeft, UserPlus, RefreshCw, Copy, Check, ScrollText, Scale, AlertTriangle, Plus, CheckCircle2, Trash2, Flag } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Modal } from '@/components/Modal';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import type { PlatformTenant } from '@/components/TenantSwitcher';

/** システム管理者 (cross_tenant_admin) 向けのテナント管理画面 (v1.0 Phase4、
 *  docs/design/v10-phase4-platform-admin-ui.md §3-4)。テナント一覧 → テナント別ユーザー一覧 →
 *  招待発行/招待URL再発行 の3階層をこの1ページ内で完結させる (別ルートに分割しない、規模的に
 *  過剰設計になるため)。v1.0 Phase5 Sprint14 (docs/design/v10-phase5-sprint14-audit-log.md §2-2) で
 *  「監査ログ」タブを追加。同じ理由で新規ルートには分割せず、既存ページ内のタブ切替で対応する。 */

type PlatformUser = {
  user_id: string;
  email: string | null;
  display_name: string;
  status: 'active' | 'suspended' | 'locked' | 'deleted';
  password_must_change: boolean;
  created_at: string;
  last_login_at: string | null;
};

type PlatformRole = { role_id: string; role_name: string };

const STATUS_LABEL: Record<PlatformUser['status'], string> = {
  active: '利用中', suspended: '停止中', locked: 'ロック中', deleted: '削除済み',
};

export function PlatformAdminPage() {
  const user = useAuthStore((s) => s.user);
  const isCrossTenantAdmin = user?.is_cross_tenant_admin === true;
  const queryClient = useQueryClient();
  // QA/design-and-code/platform-reviewer 3並列レビューで独立検出したCRITICAL反映: 全hookは
  // 早期return (末尾のJSXガード) より前で無条件に呼ぶこと (Rules of Hooks)。/platform への
  // 直接遷移・リロード時は user===null の初回レンダー→/auth/me解決後の再レンダーで hook呼び出し
  // 回数が変わってしまい "Rendered more hooks than during the previous render" でクラッシュしていた
  const [activeTab, setActiveTab] = useState<'tenants' | 'audit-logs' | 'legal-constants'>('tenants');
  const [selectedTenant, setSelectedTenant] = useState<PlatformTenant | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: tenantsData, isLoading: tenantsLoading } = useQuery<{ tenants: PlatformTenant[] }>({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiFetch('/platform/tenants'),
    enabled: isCrossTenantAdmin,
  });
  const tenants = tenantsData?.tenants ?? [];

  const { data: usersData, isLoading: usersLoading } = useQuery<{ users: PlatformUser[] }>({
    queryKey: ['platform', 'tenants', selectedTenant?.tenant_id, 'users'],
    queryFn: () => apiFetch(`/platform/tenants/${selectedTenant!.tenant_id}/users`),
    enabled: isCrossTenantAdmin && !!selectedTenant,
  });
  const users = usersData?.users ?? [];

  const resendMut = useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ setup_url: string }>(`/platform/tenants/${selectedTenant!.tenant_id}/users/${userId}/resend-setup-token`, { method: 'POST' }),
    onSuccess: (res) => {
      setSetupUrl(res.setup_url);
      setCopied(false);
      toast.success('招待URLを再発行しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '再発行に失敗しました'),
  });

  const copySetupUrl = async () => {
    if (!setupUrl) return;
    try {
      await navigator.clipboard.writeText(setupUrl);
      setCopied(true);
    } catch {
      toast.error('コピーに失敗しました。手動で選択してコピーしてください');
    }
  };

  // design書§3-4: バックエンドは各API個別にrequireCrossTenantAdminで保護済みだが、フロントでも
  // 二重防御としてガードする (§8-55型の「サイドバーは隠したが直接URL遷移すると見える」再発防止)。
  // user===null は /auth/me 到着前の一時状態 (App.tsxのuseEffect完了待ち、AppLayoutのpermsReadyと
  // 同型) のため、この間は誤って本物のシステム管理者をホームへ跳ばさないよう何も描画しない。
  // 全hookをここより前で呼び終えているため、このガードはRules of Hooksに抵触しない
  if (user === null) return null;
  if (!isCrossTenantAdmin) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <ShieldCheck size={24} className="text-brand-red" /> システム管理
        </h1>
        <p className="text-sm text-text-sub mt-1">
          全テナントの一覧・新規ユーザー招待発行・招待URL再発行を行います
        </p>
      </div>

      <div className="flex gap-1 border-b border-surface-line" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'tenants'}
          onClick={() => setActiveTab('tenants')}
          className={`min-h-[44px] px-3 text-sm font-bold border-b-2 -mb-px ${
            activeTab === 'tenants' ? 'border-brand-red text-brand-red' : 'border-transparent text-text-sub hover:text-text-body'
          }`}
        >
          テナント管理
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'audit-logs'}
          onClick={() => setActiveTab('audit-logs')}
          className={`min-h-[44px] px-3 text-sm font-bold border-b-2 -mb-px flex items-center gap-1 ${
            activeTab === 'audit-logs' ? 'border-brand-red text-brand-red' : 'border-transparent text-text-sub hover:text-text-body'
          }`}
        >
          <ScrollText size={14} /> 監査ログ
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'legal-constants'}
          onClick={() => setActiveTab('legal-constants')}
          className={`min-h-[44px] px-3 text-sm font-bold border-b-2 -mb-px flex items-center gap-1 ${
            activeTab === 'legal-constants' ? 'border-brand-red text-brand-red' : 'border-transparent text-text-sub hover:text-text-body'
          }`}
        >
          <Scale size={14} /> 法定値マスタ
        </button>
      </div>

      {activeTab === 'audit-logs' && <AuditLogSection tenants={tenants} />}
      {activeTab === 'legal-constants' && <LegalConstantsSection />}

      {activeTab === 'tenants' && (!selectedTenant ? (
        <TenantListSection tenants={tenants} loading={tenantsLoading} onSelect={setSelectedTenant} />
      ) : (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setSelectedTenant(null)}
            className="flex items-center gap-1 text-sm text-text-sub hover:text-text-body"
          >
            <ArrowLeft size={14} /> テナント一覧に戻る
          </button>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-bold">{selectedTenant.display_name} のユーザー</h2>
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="flex items-center gap-1 min-h-[36px] px-3 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover whitespace-nowrap"
            >
              <UserPlus size={16} /> 新規ユーザー招待
            </button>
          </div>

          <div className="border border-surface-line rounded-lg bg-surface-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="text-text-sub bg-surface-sunken/50 text-left">
                  <tr>
                    <th scope="col" className="px-3 py-2">表示名</th>
                    <th scope="col" className="px-3 py-2">メールアドレス</th>
                    <th scope="col" className="px-3 py-2 w-24 text-center">状態</th>
                    <th scope="col" className="px-3 py-2 w-32 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-text-sub">読み込み中…</td></tr>
                  )}
                  {!usersLoading && users.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-text-sub">ユーザーがいません</td></tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-surface-line">
                      <td className="px-3 py-2">{u.display_name}</td>
                      <td className="px-3 py-2 text-text-sub">{u.email ?? '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                          u.password_must_change ? 'bg-alert-bg text-alert-text'
                            : u.status === 'active' ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-surface-sunken text-text-sub'
                        }`}>
                          {u.password_must_change ? '招待中' : STATUS_LABEL[u.status]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {u.password_must_change && (
                          <button
                            type="button"
                            onClick={() => resendMut.mutate(u.user_id)}
                            disabled={resendMut.isPending}
                            className="inline-flex items-center gap-1 min-h-[44px] px-2 py-1 rounded border border-surface-border text-xs hover:bg-surface-sunken disabled:opacity-50"
                          >
                            <RefreshCw size={12} /> 招待URL再発行
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <PlatformFeaturesSection tenantId={selectedTenant.tenant_id} />
        </div>
      ))}

      {activeTab === 'tenants' && selectedTenant && (
        <InviteUserModal
          tenant={selectedTenant}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onCreated={(url) => {
            setInviteOpen(false);
            setSetupUrl(url);
            setCopied(false);
            queryClient.invalidateQueries({ queryKey: ['platform', 'tenants', selectedTenant.tenant_id, 'users'] });
          }}
        />
      )}

      <Modal
        open={!!setupUrl}
        title="招待URL"
        onClose={() => setSetupUrl(null)}
        size="sm"
        footer={
          <button type="button" onClick={() => setSetupUrl(null)}
            className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken focus-ring min-h-[44px] sm:min-h-[40px]">
            閉じる
          </button>
        }
      >
        <p className="text-sm text-text-body mb-2">このURLを対象ユーザー本人へ直接伝えてください (24時間有効・1回のみ利用可)。</p>
        <div className="flex items-center gap-2">
          <input readOnly value={setupUrl ?? ''} className="flex-1 min-w-0 min-h-[44px] px-2 py-1.5 border border-surface-border rounded bg-surface-sunken text-xs font-mono truncate" />
          <button type="button" onClick={copySetupUrl}
            className="shrink-0 flex items-center gap-1 min-h-[44px] px-2 py-1.5 rounded border border-surface-border text-xs hover:bg-surface-sunken">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'コピー済' : 'コピー'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function TenantListSection({ tenants, loading, onSelect }: {
  tenants: PlatformTenant[]; loading: boolean; onSelect: (t: PlatformTenant) => void;
}) {
  return (
    <div className="border border-surface-line rounded-lg bg-surface-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead className="text-text-sub bg-surface-sunken/50 text-left">
            <tr>
              <th scope="col" className="px-3 py-2">テナント名</th>
              <th scope="col" className="px-3 py-2 w-24">エディション</th>
              <th scope="col" className="px-3 py-2 w-24">状態</th>
              <th scope="col" className="px-3 py-2 w-28 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-text-sub">読み込み中…</td></tr>
            )}
            {!loading && tenants.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-text-sub">テナントがありません</td></tr>
            )}
            {tenants.map((t) => (
              <tr key={t.tenant_id} className="border-t border-surface-line">
                <td className="px-3 py-2">{t.display_name}</td>
                <td className="px-3 py-2 text-text-sub">{t.edition === 'lite' ? 'Lite版' : 'Full版'}</td>
                <td className="px-3 py-2 text-text-sub">{t.status}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onSelect(t)}
                    className="min-h-[44px] px-2 py-1 rounded border border-surface-border text-xs hover:bg-surface-sunken"
                  >
                    ユーザー管理
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type PlatformFeature = { feature_code: string; label: string; description: string; is_enabled: boolean };

/**
 * 休暇管理 (TD-26) 積み残し③: platform_admin 限定フラグ (leave_management 等) の正規の有効化経路。
 *
 * ⚠️ switch-tenant で対象テナントへ切り替えても「借用ロール」は常に company_admin になるため
 * (docs/design/v10-multi-tenant.md Chapter 6.3)、対象テナントの `/settings/features` からは
 * platform_admin 限定フラグを編集できない。この画面はシステム管理者 (cross-tenant-admin) 自身の
 * セッションのまま、対象テナントを跨いで直接操作する新設の
 * GET/PATCH /platform/tenants/:tenantId/features を叩く。
 *
 * company_admin が編集可能なフラグはここには出さない (バックエンドが editable_by='platform_admin'
 * のみを返す、routes/platform.ts 参照) — company_admin編集可のフラグは従来通り switch-tenant +
 * `/settings/features` で操作する、単一の正典を保つ。
 */
function PlatformFeaturesSection({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ features: PlatformFeature[] }>({
    queryKey: ['platform', 'tenants', tenantId, 'features'],
    queryFn: () => apiFetch(`/platform/tenants/${tenantId}/features`),
  });
  const features = data?.features ?? [];

  const toggleMut = useMutation({
    mutationFn: (v: { code: string; is_enabled: boolean }) =>
      apiFetch(`/platform/tenants/${tenantId}/features/${v.code}`, { method: 'PATCH', body: JSON.stringify({ is_enabled: v.is_enabled }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId, 'features'] }); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Flag size={16} className="text-text-sub" />
        <h3 className="text-sm font-bold">プラットフォーム管理者限定の利用機能</h3>
      </div>
      <p className="text-xs text-text-sub">
        テナント側の「設定 &gt; 利用機能」からは変更できないフラグです。ここでの変更は対象テナントの監査ログにも記録されます。
      </p>

      {isLoading ? <div className="text-sm text-text-sub">読み込み中…</div> : features.length === 0 ? (
        <div className="border border-surface-line rounded-lg bg-surface-card p-4 text-sm text-text-sub">
          対象のフラグはありません (エディションに合わないものを含め全て非該当)。
        </div>
      ) : (
        <div className="border border-surface-line rounded-lg bg-surface-card divide-y divide-surface-line">
          {features.map((f) => (
            <div key={f.feature_code} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-text-body">{f.label}</div>
                {f.description && <div className="text-xs text-text-sub mt-0.5">{f.description}</div>}
              </div>
              <ToggleSwitch
                checked={f.is_enabled}
                disabled={toggleMut.isPending}
                onChange={(v) => toggleMut.mutate({ code: f.feature_code, is_enabled: v })}
                ariaLabel={`${f.label}を${f.is_enabled ? '無効化' : '有効化'}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InviteUserModal({ tenant, open, onClose, onCreated }: {
  tenant: PlatformTenant; open: boolean; onClose: () => void; onCreated: (setupUrl: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);

  const { data: rolesData } = useQuery<{ roles: PlatformRole[] }>({
    queryKey: ['platform', 'tenants', tenant.tenant_id, 'roles'],
    queryFn: () => apiFetch(`/platform/tenants/${tenant.tenant_id}/roles`),
    enabled: open,
  });
  const roles = rolesData?.roles ?? [];

  const createMut = useMutation({
    mutationFn: () => apiFetch<{ setup_url: string }>(`/platform/tenants/${tenant.tenant_id}/users`, {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), display_name: displayName.trim(), role_ids: roleIds }),
    }),
    onSuccess: (res) => {
      toast.success('招待を発行しました');
      setEmail(''); setDisplayName(''); setRoleIds([]);
      onCreated(res.setup_url);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '招待発行に失敗しました'),
  });

  const toggleRole = (roleId: string) => {
    setRoleIds((prev) => prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]);
  };

  return (
    <Modal
      open={open}
      title={`${tenant.display_name} へ新規ユーザーを招待`}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={createMut.isPending}
            className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
            キャンセル
          </button>
          <button
            type="button"
            onClick={() => {
              if (!email.trim() || !displayName.trim()) { toast.error('メールアドレスと表示名は必須です'); return; }
              if (roleIds.length === 0) { toast.error('ロールを1件以上選択してください'); return; }
              createMut.mutate();
            }}
            disabled={createMut.isPending}
            className="h-10 px-5 rounded-md text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px] bg-brand-red"
          >
            {createMut.isPending ? '発行中…' : '招待を発行'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="invite-email" className="block text-xs text-text-sub mb-1">メールアドレス</label>
          <input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
        </div>
        <div>
          <label htmlFor="invite-name" className="block text-xs text-text-sub mb-1">表示名</label>
          <input id="invite-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
        </div>
        <fieldset>
          <legend className="block text-xs text-text-sub mb-1">ロール</legend>
          <div className="space-y-1">
            {roles.length === 0 && <p className="text-xs text-text-sub">ロールを読み込み中、または利用可能なロールがありません</p>}
            {roles.map((r) => (
              <label key={r.role_id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={roleIds.includes(r.role_id)} onChange={() => toggleRole(r.role_id)} />
                {r.role_name}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}

/** v1.0 Phase5 Sprint14 (docs/design/v10-phase5-sprint14-audit-log.md §2-2): audit_logs_platform 一覧。
 *  「もっと見る」で追加ロード・累積表示するため useInfiniteQuery を使う (フィルタ変更時は queryKey が
 *  変わり自動的に1ページ目からやり直される)。 */

type AuditLogEntry = {
  id: string;
  event_type: string;
  tenant_id: string | null;
  tenant_display_name: string | null;
  actor_tenant_id: string | null;
  actor_tenant_display_name: string | null;
  user_id: string | null;
  user_display_name: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
};

const AUDIT_LOG_LIMIT = 50;

const EVENT_TYPE_LABEL: Record<string, string> = {
  'tenant.switched': 'テナント切替',
  'tenant.switch_denied': 'テナント切替拒否',
  'cross_tenant_admin.granted': 'システム管理者昇格',
  'cross_tenant_admin.tenant_granted': 'テナント権限追加付与',
  'platform.admins_viewed': '管理者一覧閲覧',
  'tenant.users_viewed': 'テナントユーザー一覧閲覧',
  'user.created': 'ユーザー招待発行',
  'user.setup_token_resent': '招待URL再発行',
  // design-and-codeレビュー指摘 (HIGH): auth.ts で既に記録している3イベントがフィルタ選択肢から
  // 漏れていた (Sprint14スコープ外の既存イベントだが、表示UI自体はSprint14で新設のため網羅する)
  'user.password_reset': 'パスワードリセット（管理者操作）',
  'user.setup_password_failed': '初回パスワード設定失敗',
  'user.setup_password_completed': 'パスワード設定完了',
  // パスワード5回失敗ロック機能 (§8-190) で記録漏れが判明。web-static-dictionaries.test.ts が
  // writeAuditLogPlatform の実発行イベントとの集合一致を機械検証している。
  'user.locked': 'ログイン失敗超過によるアカウントロック',
  // ⚠️ v0.4 追加要件⑲ で気づいた既存の drift (§8-152 の8例目): F-6 (D-195) の法定値マスタ／
  // 参照祝日マスタで追加された6種がここに未登録で、監査ログ画面に英字の生コードが出ていた。
  // 以降は apps/api/src/lib/__tests__/web-static-dictionaries.test.ts が集合一致を機械検証する。
  'platform.legal_constants_verified': '法定値マスタ 確認記録',
  'platform.legal_constant_created': '法定値マスタ 追加',
  'platform.legal_constant_updated': '法定値マスタ 更新',
  'platform.reference_holiday_created': '参照祝日 追加',
  'platform.reference_holiday_updated': '参照祝日 更新',
  'platform.reference_holiday_deleted': '参照祝日 削除',
  // v0.4 追加要件⑲ (D-500): セルフリセット。⚠️ details_json には purpose / origin / outcome しか
  // 入れていない (生トークン・メールアドレス・line_user_id の生値は入れない)。
  'user.password_reset_requested': 'パスワード再設定 申請',
  'user.password_reset_throttled': 'パスワード再設定 制限超過',
  'user.password_reset_issued': 'パスワード再設定 リンク発行',
  'user.password_reset_delivery_failed': 'パスワード再設定 配信失敗',
  // 休暇管理 (TD-26) 積み残し③: platform_admin限定フラグの有効化経路 (routes/platform.ts
  // GET/PATCH /tenants/:tenantId/features)。
  'platform.tenant_feature_updated': '利用機能設定 変更 (platform_admin代行)',
};

function AuditLogSection({ tenants }: { tenants: PlatformTenant[] }) {
  const [tenantFilter, setTenantFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    { logs: AuditLogEntry[]; has_more: boolean }
  >({
    queryKey: ['platform', 'audit-logs', tenantFilter, eventTypeFilter],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (tenantFilter) params.set('tenant_id', tenantFilter);
      if (eventTypeFilter) params.set('event_type', eventTypeFilter);
      params.set('limit', String(AUDIT_LOG_LIMIT));
      params.set('offset', String(pageParam));
      return apiFetch(`/platform/audit-logs?${params.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.has_more ? allPages.length * AUDIT_LOG_LIMIT : undefined),
  });

  const logs = data?.pages.flatMap((p) => p.logs) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select value={tenantFilter || 'all'} onValueChange={(v) => setTenantFilter(v === 'all' ? '' : v)}>
          <SelectTrigger aria-label="テナントで絞り込み" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全テナント</SelectItem>
            {tenants.map((t) => (
              <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.display_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventTypeFilter || 'all'} onValueChange={(v) => setEventTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger aria-label="イベント種別で絞り込み" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全イベント種別</SelectItem>
            {Object.entries(EVENT_TYPE_LABEL).map(([code, label]) => (
              <SelectItem key={code} value={code}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-surface-line rounded-lg bg-surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-text-sub bg-surface-sunken/50 text-left">
              <tr>
                <th scope="col" className="relative px-3 py-2 w-40">発生日時</th>
                <th scope="col" className="relative px-3 py-2">イベント</th>
                <th scope="col" className="relative px-3 py-2">操作者</th>
                <th scope="col" className="relative px-3 py-2">対象テナント</th>
                <th scope="col" className="relative px-3 py-2">詳細</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-text-sub">読み込み中…</td></tr>
              )}
              {/* QAレビュー指摘 (MEDIUM): エラー時も空状態と同じ表示になっており「本当に0件」と
                  「取得に失敗した」の区別がつかなかった (監査ログは調査用途のため区別が重要) */}
              {!isLoading && isError && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-alert-text">読み込みに失敗しました。時間をおいて再度お試しください</td></tr>
              )}
              {!isLoading && !isError && logs.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-text-sub">該当する監査ログがありません</td></tr>
              )}
              {logs.map((log) => (
                <tr key={log.id} className="border-t border-surface-line align-top">
                  <td className="px-3 py-2 text-text-sub whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('ja-JP')}
                  </td>
                  <td className="px-3 py-2">{EVENT_TYPE_LABEL[log.event_type] ?? log.event_type}</td>
                  <td className="px-3 py-2 text-text-sub">{log.user_display_name ?? log.user_id ?? '—'}</td>
                  <td className="px-3 py-2 text-text-sub">{log.tenant_display_name ?? log.tenant_id ?? '—'}</td>
                  <td
                    className="px-3 py-2 text-text-sub font-mono text-xs max-w-xs truncate"
                    title={log.details ? JSON.stringify(log.details) : undefined}
                  >
                    {log.details ? JSON.stringify(log.details) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="min-h-[44px] px-4 py-2 rounded border border-surface-border text-sm hover:bg-surface-sunken disabled:opacity-50"
          >
            {isFetchingNextPage ? '読み込み中…' : 'もっと見る'}
          </button>
        </div>
      )}
    </div>
  );
}

/** v0.4 F-6 (D-195): 法定値マスタ (legal_constants) の管理タブ。テナント側 (`routes/legal-constants.ts`)
 *  は読み取り専用のまま維持し、書込みはこのタブ (`requireCrossTenantAdmin` 限定) からのみ行う —
 *  explanation が D-89 で AI プロンプトに引用されるため、テナントに書込み経路があると1テナントの
 *  編集が全テナントのAI出力を汚染する (§0-6b-2 のセキュリティ判断を踏まえた設計、routes/platform.ts 参照)。 */

type LegalConstant = {
  constant_key: string;
  jurisdiction: string;
  value: unknown;
  unit: string | null;
  label: string;
  explanation: string;
  source_citation: string;
  last_verified_on: string;
};

type ValueEntry = { key: string; value: string };

/** value (JSON object) ⇔ フォームの key/value 行の相互変換。汎用JSONエディタは過剰設計のため、
 *  トップレベルのキーだけを行として編集できる簡易な key/value サブテーブルに留める (§6d)。 */
const objectToEntries = (v: unknown): ValueEntry[] => {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return Object.entries(v as Record<string, unknown>).map(([key, val]) => ({
      key, value: typeof val === 'string' ? val : JSON.stringify(val),
    }));
  }
  return [];
};

const entriesToObject = (entries: ValueEntry[]): Record<string, unknown> => {
  const obj: Record<string, unknown> = {};
  for (const { key, value } of entries) {
    if (!key.trim()) continue;
    let parsed: unknown = value;
    try { parsed = JSON.parse(value); } catch { /* JSONとして解釈できない場合は文字列としてそのまま保持 (例: "22:00") */ }
    obj[key.trim()] = parsed;
  }
  return obj;
};

function LegalConstantsSection() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<{ constants: LegalConstant[] }>({
    queryKey: ['platform', 'legal-constants'],
    queryFn: () => apiFetch('/platform/legal-constants'),
  });
  const constants = data?.constants ?? [];

  // undefined = モーダルを閉じている、null = 新規追加、LegalConstant = 既存の編集
  const [editTarget, setEditTarget] = useState<LegalConstant | null | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const verifyMut = useMutation({
    mutationFn: () => apiFetch<{ verified: string[] }>('/platform/legal-constants/verify', {
      method: 'POST',
      body: JSON.stringify({ constant_keys: [...selected], verified_on: new Date().toISOString().slice(0, 10) }),
    }),
    onSuccess: (res) => {
      toast.success(`${res.verified.length}件の確認日を更新しました`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['platform', 'legal-constants'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確認日の更新に失敗しました'),
  });

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="p-3 rounded bg-alert-bg border border-alert-border">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-alert-text flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-alert-text">
            この一覧は全テナント共通のマスタです (テナント別の個別設定ではありません)。各値の説明文はAIコメント生成時のプロンプトに引用されるため、変更内容は全テナントのAI出力に反映されます。
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <button type="button" onClick={() => setEditTarget(null)}
          className="flex items-center gap-1 min-h-[44px] px-3 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover whitespace-nowrap">
          <Plus size={16} /> 新規追加
        </button>
        <button type="button" onClick={() => verifyMut.mutate()} disabled={selected.size === 0 || verifyMut.isPending}
          className="flex items-center gap-1 min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm hover:bg-surface-sunken disabled:opacity-50 whitespace-nowrap">
          <CheckCircle2 size={14} /> {verifyMut.isPending ? '更新中…' : `選択した${selected.size}件の確認日を更新`}
        </button>
      </div>

      <div className="border border-surface-line rounded-lg bg-surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-text-sub bg-surface-sunken/50 text-left">
              <tr>
                <th scope="col" className="relative px-3 py-2 w-10"><span className="sr-only">選択</span></th>
                <th scope="col" className="relative px-3 py-2">キー / 名称</th>
                <th scope="col" className="relative px-3 py-2">値</th>
                <th scope="col" className="relative px-3 py-2 w-20">単位</th>
                <th scope="col" className="relative px-3 py-2 w-28">最終確認日</th>
                <th scope="col" className="relative px-3 py-2 w-20 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={6} className="px-3 py-6 text-center text-text-sub">読み込み中…</td></tr>)}
              {!isLoading && isError && (<tr><td colSpan={6} className="px-3 py-6 text-center text-alert-text">読み込みに失敗しました。時間をおいて再度お試しください</td></tr>)}
              {!isLoading && !isError && constants.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-text-sub">法定値がまだ登録されていません</td></tr>
              )}
              {constants.map((con) => (
                <tr key={con.constant_key} className="border-t border-surface-line align-top">
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(con.constant_key)} onChange={() => toggleSelect(con.constant_key)}
                      aria-label={`${con.label} を選択`} className="min-w-[20px] min-h-[20px]" />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-text-main">{con.label}</div>
                    <div className="text-xs text-text-sub font-mono">{con.constant_key}</div>
                  </td>
                  <td className="px-3 py-2 text-text-sub font-mono text-xs max-w-xs break-all">
                    {JSON.stringify(con.value)}
                  </td>
                  <td className="px-3 py-2 text-text-sub">{con.unit ?? '—'}</td>
                  <td className="px-3 py-2 text-text-sub whitespace-nowrap">{con.last_verified_on}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => setEditTarget(con)}
                      className="min-h-[44px] px-2 py-1 rounded border border-surface-border text-xs hover:bg-surface-sunken">
                      編集
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LegalConstantEditModal
        constant={editTarget ?? null}
        open={editTarget !== undefined}
        onClose={() => setEditTarget(undefined)}
        onSaved={() => {
          setEditTarget(undefined);
          qc.invalidateQueries({ queryKey: ['platform', 'legal-constants'] });
        }}
      />
    </div>
  );
}

function LegalConstantEditModal({ constant, open, onClose, onSaved }: {
  constant: LegalConstant | null; // null = 新規作成
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [constantKey, setConstantKey] = useState('');
  const [jurisdiction, setJurisdiction] = useState('JP');
  const [label, setLabel] = useState('');
  const [unit, setUnit] = useState('');
  const [explanation, setExplanation] = useState('');
  const [sourceCitation, setSourceCitation] = useState('');
  const [lastVerifiedOn, setLastVerifiedOn] = useState('');
  const [entries, setEntries] = useState<ValueEntry[]>([{ key: '', value: '' }]);

  // 開いた瞬間のスナップショットから詰め直す (ProfilePage.tsx の startEditingName と同型パターン、
  // useEffectでの継続同期ではなくopen契機の1回だけの初期化にすることで編集中の入力を保持する)
  useEffect(() => {
    if (!open) return;
    setConstantKey(constant?.constant_key ?? '');
    setJurisdiction(constant?.jurisdiction ?? 'JP');
    setLabel(constant?.label ?? '');
    setUnit(constant?.unit ?? '');
    setExplanation(constant?.explanation ?? '');
    setSourceCitation(constant?.source_citation ?? '');
    setLastVerifiedOn(constant?.last_verified_on ?? new Date().toISOString().slice(0, 10));
    const initialEntries = objectToEntries(constant?.value);
    setEntries(initialEntries.length > 0 ? initialEntries : [{ key: '', value: '' }]);
  }, [open, constant]);

  const isNew = constant === null;

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/platform/legal-constants/${encodeURIComponent(constantKey.trim())}`, {
      method: 'PUT',
      body: JSON.stringify({
        jurisdiction: jurisdiction.trim() || undefined,
        value: entriesToObject(entries),
        unit: unit.trim() || null,
        label: label.trim(),
        explanation: explanation.trim(),
        source_citation: sourceCitation.trim(),
        last_verified_on: lastVerifiedOn,
      }),
    }),
    onSuccess: () => { toast.success('保存しました'); onSaved(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const handleSave = () => {
    if (!constantKey.trim() || !label.trim() || !explanation.trim() || !sourceCitation.trim() || !lastVerifiedOn) {
      toast.error('必須項目 (キー・名称・説明・根拠・最終確認日) を入力してください');
      return;
    }
    saveMut.mutate();
  };

  return (
    <Modal
      open={open}
      title={isNew ? '法定値の新規追加' : `法定値の編集: ${constant.label}`}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saveMut.isPending}
            className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
            キャンセル
          </button>
          <button type="button" onClick={handleSave} disabled={saveMut.isPending}
            className="h-10 px-5 rounded-md text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px] bg-brand-red">
            {saveMut.isPending ? '保存中…' : '保存'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="lc-key" className="block text-xs text-text-sub mb-1">キー (constant_key)*</label>
          <input id="lc-key" value={constantKey} onChange={(e) => setConstantKey(e.target.value)}
            disabled={!isNew}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm font-mono disabled:bg-surface-sunken disabled:text-text-sub focus-ring" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="lc-label" className="block text-xs text-text-sub mb-1">名称*</label>
            <input id="lc-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200}
              className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
          </div>
          <div>
            <label htmlFor="lc-jurisdiction" className="block text-xs text-text-sub mb-1">法域</label>
            <input id="lc-jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} maxLength={20}
              className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="block text-xs text-text-sub">値</span>
            <button type="button" onClick={() => setEntries((prev) => [...prev, { key: '', value: '' }])}
              className="text-xs text-chrome-base hover:underline min-h-[44px] px-1">+ 項目を追加</button>
          </div>
          <div className="space-y-1.5">
            {entries.map((entry, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input value={entry.key} placeholder="キー"
                  onChange={(e) => setEntries((prev) => prev.map((en, idx) => (idx === i ? { ...en, key: e.target.value } : en)))}
                  className="w-1/3 px-2 py-1.5 border border-surface-border rounded text-xs font-mono focus-ring" />
                <input value={entry.value} placeholder="値"
                  onChange={(e) => setEntries((prev) => prev.map((en, idx) => (idx === i ? { ...en, value: e.target.value } : en)))}
                  className="flex-1 px-2 py-1.5 border border-surface-border rounded text-xs font-mono focus-ring" />
                <button type="button" onClick={() => setEntries((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label="この項目を削除"
                  className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center text-text-sub hover:text-danger-text">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="lc-unit" className="block text-xs text-text-sub mb-1">単位</label>
          <input id="lc-unit" value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={50}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
        </div>
        <div>
          <label htmlFor="lc-explanation" className="block text-xs text-text-sub mb-1">説明 (AIコメントのプロンプトに引用されます)*</label>
          <textarea id="lc-explanation" value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={3} maxLength={2000}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
        </div>
        <div>
          <label htmlFor="lc-source" className="block text-xs text-text-sub mb-1">根拠・出典*</label>
          <input id="lc-source" value={sourceCitation} onChange={(e) => setSourceCitation(e.target.value)} maxLength={500}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
        </div>
        <div>
          <label htmlFor="lc-verified" className="block text-xs text-text-sub mb-1">最終確認日*</label>
          <input id="lc-verified" type="date" value={lastVerifiedOn} onChange={(e) => setLastVerifiedOn(e.target.value)}
            className="w-full px-2 py-1.5 border border-surface-border rounded text-sm focus-ring" />
        </div>
      </div>
    </Modal>
  );
}
